import { useState } from 'react'
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Bell,
  Search,
  Menu,
  ChevronDown,
  FileText,
  Settings,
  BarChart3,
  List,
  Filter,
  MoreHorizontal,
  Terminal,
  Smartphone,
  Webhook,
} from 'lucide-react'
import ArtifactStateOverlay from './ArtifactStateOverlay'
import FallbackNotice from './ui/FallbackNotice'

/* ═══════════════════════════════════════════════
   WIREFRAME DATA INTERFACES
   ═══════════════════════════════════════════════ */

interface WireframeWidget {
  type: string
  label: string
  value?: string
  change?: string
  columns?: string[]
  fields?: string[]
  variant?: string
  data?: string[]
  rows?: Record<string, string>[]
  tabs?: string[]
  filters?: string[]
  items?: { title: string; subtitle: string }[]
}

interface WireframePage {
  name: string
  route: string
  layout: string
  widgets: WireframeWidget[]
}

interface PrototypeCommand {
  command: string
  description?: string
  example_output?: string
}

interface PrototypeEndpoint {
  method: string
  path: string
  description?: string
  request_example?: string
  response_example?: string
}

type InterfaceType = 'web' | 'cli' | 'mobile' | 'api' | 'desktop'

interface PrototypeData {
  /** Tipo de interface detectado pelo agente — decide qual renderer usar. */
  interface_type?: InterfaceType
  title: string
  description?: string
  pages?: WireframePage[]
  /** Comandos, quando interface_type = 'cli'. */
  commands?: PrototypeCommand[]
  /** Endpoints, quando interface_type = 'api'. */
  endpoints?: PrototypeEndpoint[]
  theme?: string
  framework?: string
  /** Marcado pelo backend quando o protótipo veio do fallback determinístico. */
  is_fallback?: boolean
}

interface PrototypeCanvasProps {
  data: PrototypeData | null
  darkMode?: boolean
  isRegenerating?: boolean
  onRegenerate?: () => void
}

/* ═══════════════════════════════════════════════
   PAGE ICONS
   ═══════════════════════════════════════════════ */

const PAGE_ICONS: Record<string, React.ElementType> = {
  Dashboard: LayoutDashboard,
  Produtos: ShoppingCart,
  Pedidos: List,
  Usuários: Users,
  Conteúdo: FileText,
  Listagem: List,
  Detalhes: FileText,
  Configurações: Settings,
  Analytics: BarChart3,
}

/* ═══════════════════════════════════════════════
   WIREFRAME ATOMIC COMPONENTS
   ═══════════════════════════════════════════════ */

/** Gray placeholder rectangle */
function WFRect({ w, h, className = '' }: { w: string | number; h: string | number; className?: string }) {
  return <div className={`bg-gray-200 dark:bg-gray-700 rounded-sm ${className}`} style={{ width: w, height: h }} />
}

/** Placeholder text line — the core of wireframe text representation */
function WFText({ w = '80%', h = 8, className = '' }: { w?: string | number; h?: number; className?: string }) {
  return <WFRect w={w} h={h} className={`${className}`} />
}

/** Image/avatar placeholder — rectangle with X */
function WFXBox({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <div
      className={`relative bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0">
        <line x1="4" y1="4" x2={size - 4} y2={size - 4} stroke="currentColor" className="text-gray-300 dark:text-gray-600" strokeWidth="1" />
        <line x1={size - 4} y1="4" x2="4" y2={size - 4} stroke="currentColor" className="text-gray-300 dark:text-gray-600" strokeWidth="1" />
      </svg>
    </div>
  )
}

/** Checkbox wireframe [ ] */
function WFCheckbox({ checked = false }: { checked?: boolean }) {
  return (
    <div className="w-4 h-4 border border-gray-400 dark:border-gray-500 flex items-center justify-center text-[10px] text-gray-400">
      {checked ? '×' : ''}
    </div>
  )
}

/** Button wireframe — primary (filled) or secondary (outline) */
function WFButton({ label, primary = false, className = '' }: { label: string; primary?: boolean; className?: string }) {
  return (
    <button
      className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
        primary
          ? 'bg-gray-700 dark:bg-gray-500 text-white'
          : 'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
      } ${className}`}
    >
      {label}
    </button>
  )
}

/* ═══════════════════════════════════════════════
   WIREFRAME WIDGET RENDERERS
   ═══════════════════════════════════════════════ */

function WireframeWidgetRenderer({ widget, darkMode }: { widget: WireframeWidget; darkMode: boolean }) {
  const cardBorder = darkMode ? 'border-gray-700' : 'border-gray-300'
  const cardBg = darkMode ? 'bg-gray-900' : 'bg-white'
  const sectionBg = darkMode ? 'bg-gray-800' : 'bg-gray-50'
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-400'

  switch (widget.type) {
    case 'kpi':
    case 'stats': {
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          {/* Header: icon + label */}
          <div className="flex items-center justify-between mb-4">
            <WFText w={80} h={10} />
            <WFXBox size={28} />
          </div>
          {/* Big value */}
          <WFText w={100} h={24} className="mb-3" />
          {/* Trend */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400">▲</span>
            <WFText w={50} h={8} />
          </div>
        </div>
      )
    }

    case 'chart':
    case 'chart_bar': {
      const labels = widget.data || ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun']
      const bars = [35, 55, 40, 70, 50, 85, 65, 90, 55, 75, 60, 95].slice(0, labels.length)
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          {/* Title */}
          <div className="flex items-center justify-between mb-4">
            <WFText w={120} h={12} />
            <MoreHorizontal size={14} className={textMuted} />
          </div>
          {/* Chart area with Y-axis grid lines */}
          <div className="relative h-40 border-b border-l border-gray-300 dark:border-gray-600">
            {/* Horizontal grid lines */}
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute w-full border-t border-dashed border-gray-200 dark:border-gray-700"
                style={{ bottom: `${(i + 1) * 25}%` }}
              />
            ))}
            {/* Bars */}
            <div className="absolute inset-0 flex items-end gap-2 px-2 pb-0">
              {bars.map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'} rounded-t-sm`}
                    style={{ height: `${h}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* X-axis labels */}
          <div className="flex justify-between mt-2 px-1">
            {labels.map((m) => (
              <WFText key={m} w={24} h={6} />
            ))}
          </div>
          <p className={`mt-2 text-[10px] italic ${textMuted}`}>[Wireframe — Bar Chart]</p>
        </div>
      )
    }

    case 'chart_line': {
      const lineLabels = widget.data || ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun']
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <WFText w={120} h={12} />
            <MoreHorizontal size={14} className={textMuted} />
          </div>
          <div className="relative h-40 border-b border-l border-gray-300 dark:border-gray-600">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute w-full border-t border-dashed border-gray-200 dark:border-gray-700"
                style={{ bottom: `${(i + 1) * 25}%` }}
              />
            ))}
            <svg className="absolute inset-0 w-full h-full px-2" viewBox="0 0 300 100" preserveAspectRatio="none">
              <polyline
                fill="none"
                stroke={darkMode ? '#9CA3AF' : '#6B7280'}
                strokeWidth="2"
                points="10,70 60,40 110,55 160,20 210,35 260,15 290,25"
              />
              <polyline
                fill="none"
                stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                strokeWidth="2"
                strokeDasharray="4 4"
                points="10,80 60,60 110,70 160,50 210,60 260,40 290,50"
              />
            </svg>
          </div>
          <div className="flex justify-between mt-2 px-1">
            {lineLabels.map((m) => (
              <WFText key={m} w={24} h={6} />
            ))}
          </div>
          <p className={`mt-2 text-[10px] italic ${textMuted}`}>[Wireframe — Line Chart]</p>
        </div>
      )
    }

    case 'chart_pie': {
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <WFText w={120} h={12} />
            <MoreHorizontal size={14} className={textMuted} />
          </div>
          <div className="flex items-center justify-center h-40">
            <svg width="120" height="120" viewBox="0 0 120 120">
              {/* Background circle */}
              <circle cx="60" cy="60" r="45" fill="none" stroke={darkMode ? '#374151' : '#E5E7EB'} strokeWidth="20" />
              {/* Segment 1 */}
              <circle cx="60" cy="60" r="45" fill="none" stroke={darkMode ? '#6B7280' : '#9CA3AF'} strokeWidth="20" strokeDasharray="70 283" />
              {/* Segment 2 */}
              <circle cx="60" cy="60" r="45" fill="none" stroke={darkMode ? '#4B5563' : '#D1D5DB'} strokeWidth="20" strokeDasharray="55 283" strokeDashoffset="-70" />
              {/* Segment 3 */}
              <circle cx="60" cy="60" r="45" fill="none" stroke={darkMode ? '#9CA3AF' : '#F3F4F6'} strokeWidth="20" strokeDasharray="40 283" strokeDashoffset="-125" />
            </svg>
          </div>
          {/* Legend */}
          <div className="flex justify-center gap-3 mt-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-1">
                <WFRect w={8} h={8} className="rounded-sm" />
                <WFText w={40} h={6} />
              </div>
            ))}
          </div>
          <p className={`mt-2 text-[10px] italic ${textMuted}`}>[Wireframe — Pie Chart]</p>
        </div>
      )
    }

    case 'table':
    case 'data_table': {
      const cols = widget.columns || ['Nome', 'Status', 'Data']
      const rowCount = widget.rows?.length || 4
      return (
        <div className={`${cardBg} border ${cardBorder} overflow-hidden`}>
          {/* Table header */}
          <div className={`px-5 py-4 border-b ${cardBorder} flex items-center justify-between`}>
            <WFText w={100} h={14} />
            <WFButton label="Novo" primary />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${sectionBg} border-b ${cardBorder}`}>
                  <th className="px-4 py-3 text-left">
                    <WFCheckbox />
                  </th>
                  {cols.map((c) => (
                    <th key={c} className={`px-4 py-3 text-left text-[10px] font-semibold ${textMuted} uppercase tracking-wider`}>
                      {c}
                    </th>
                  ))}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {Array.from({ length: rowCount }).map((_, i) => (
                  <tr key={i} className={`${darkMode ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3">
                      <WFCheckbox />
                    </td>
                    {cols.map((_, ci) => (
                      <td key={ci} className="px-4 py-3">
                        <WFText w={60 + (ci % 3) * 20} h={8} />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <WFText w={40} h={8} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className={`px-5 py-3 border-t ${cardBorder} flex items-center justify-between`}>
            <WFText w={80} h={8} />
            <div className="flex items-center gap-1">
              {['<', '1', '2', '3', '>'].map((p) => (
                <div
                  key={p}
                  className={`w-7 h-7 flex items-center justify-center text-[10px] border ${
                    p === '1' ? 'bg-gray-700 text-white border-gray-700' : `${cardBg} ${cardBorder} text-gray-500`
                  }`}
                >
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }

    case 'search':
    case 'search_bar': {
      return (
        <div className={`flex items-center gap-2 px-4 py-2.5 border ${cardBorder} ${cardBg}`}>
          <Search size={16} className={textMuted} />
          <WFText w={120} h={10} />
          {widget.filters && (
            <>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
              <Filter size={14} className={textMuted} />
              <WFText w={50} h={8} />
              <ChevronDown size={14} className={textMuted} />
            </>
          )}
        </div>
      )
    }

    case 'button': {
      return <WFButton label={widget.label} primary={widget.variant === 'primary'} />
    }

    case 'filter': {
      return (
        <button className={`flex items-center gap-2 px-4 py-2.5 border ${cardBorder} ${cardBg} text-sm text-gray-500 dark:text-gray-400`}>
          <Filter size={14} />
          <WFText w={60} h={10} />
          <ChevronDown size={14} />
        </button>
      )
    }

    case 'header': {
      return (
        <div>
          <WFText w={160} h={14} className="mb-1" />
          <WFText w={80} h={8} />
        </div>
      )
    }

    case 'form':
    case 'form_group': {
      return (
        <div className={`${cardBg} border ${cardBorder} p-6 space-y-4`}>
          <WFText w={120} h={14} className="mb-2" />
          {(widget.fields || ['Campo 1', 'Campo 2']).map((f, i) => (
            <div key={f + i} className="space-y-1.5">
              <WFText w={60} h={8} />
              <div className={`w-full h-10 border ${cardBorder} ${cardBg} rounded-sm`} />
            </div>
          ))}
          <div className="pt-2 flex gap-2">
            <WFButton label="Salvar" primary />
            <WFButton label="Cancelar" />
          </div>
        </div>
      )
    }

    case 'list': {
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          <WFText w={100} h={14} className="mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <WFXBox size={36} className="rounded-full" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <WFText w={80} h={10} />
                  <WFText w={120} h={8} />
                </div>
                <WFText w={40} h={8} />
              </div>
            ))}
          </div>
        </div>
      )
    }

    case 'breadcrumb': {
      const parts = widget.label.split(' / ')
      return (
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          {parts.map((part, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className={i === parts.length - 1 ? 'text-gray-800 dark:text-gray-200 font-medium' : ''}>{part}</span>
              {i < parts.length - 1 && <span className="text-gray-300 dark:text-gray-600">/</span>}
            </span>
          ))}
        </div>
      )
    }

    case 'tabs': {
      const tabList = widget.tabs || ['Tab 1', 'Tab 2', 'Tab 3']
      return (
        <div className="flex items-center gap-0 border-b border-gray-200 dark:border-gray-700">
          {tabList.map((tab, i) => (
            <button
              key={tab}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                i === 0
                  ? 'border-gray-700 text-gray-800 dark:text-gray-200 dark:border-gray-400'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      )
    }

    case 'card_grid': {
      return (
        <div className={`${cardBg} border ${cardBorder} p-5`}>
          <WFText w={100} h={14} className="mb-4" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(widget.items || [
              { title: 'Item A', subtitle: 'Descrição A' },
              { title: 'Item B', subtitle: 'Descrição B' },
              { title: 'Item C', subtitle: 'Descrição C' },
            ]).map((_item, i) => (
              <div key={i} className={`border ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-3 space-y-2`}>
                <WFXBox size={48} className="w-full" />
                <WFText w={80} h={10} />
                <WFText w={60} h={8} />
              </div>
            ))}
          </div>
        </div>
      )
    }

    default: {
      return (
        <div className={`${cardBg} border ${cardBorder} p-4`}>
          <WFText w={120} h={12} />
        </div>
      )
    }
  }
}

/* ═══════════════════════════════════════════════
   MAIN CANVAS
   ═══════════════════════════════════════════════ */

export default function PrototypeCanvas({ data, darkMode = false, isRegenerating = false, onRegenerate }: PrototypeCanvasProps) {
  const [activePage, setActivePage] = useState(0)

  // Renderer por tipo de interface detectado pelo agente. web/desktop caem no
  // wireframe padrão; cli/api/mobile têm renderers próprios.
  const kind: InterfaceType = data?.interface_type ?? 'web'
  if (kind === 'cli' || kind === 'api' || kind === 'mobile') {
    return (
      <NonWebPrototype
        kind={kind}
        data={data}
        darkMode={darkMode}
        isRegenerating={isRegenerating}
        onRegenerate={onRegenerate}
      />
    )
  }

  const bgMain = darkMode ? 'bg-gray-950' : 'bg-gray-100'
  const bgSidebar = darkMode ? 'bg-gray-900' : 'bg-gray-50'
  const textPrimary = darkMode ? 'text-gray-200' : 'text-gray-800'
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500'
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-400'
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200'
  const headerBg = darkMode ? 'bg-gray-900' : 'bg-white'
  const inputBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
  const hoverBg = darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'

  const title = data?.title || 'Sistema Web'
  const description = data?.description || 'Protótipo gerado pelo agente'
  const pages = data?.pages || []
  const currentPage = pages[activePage] || { name: 'Dashboard', widgets: [] }

  const kpiWidgets = currentPage.widgets?.filter((w) => w.type === 'kpi' || w.type === 'stats') || []
  const chartWidgets = currentPage.widgets?.filter((w) => ['chart', 'chart_bar', 'chart_line', 'chart_pie'].includes(w.type)) || []
  const tableWidgets = currentPage.widgets?.filter((w) => w.type === 'table' || w.type === 'data_table') || []
  const listWidgets = currentPage.widgets?.filter((w) => w.type === 'list') || []
  const formWidgets = currentPage.widgets?.filter((w) => w.type === 'form' || w.type === 'form_group') || []
  const otherWidgets = currentPage.widgets?.filter(
    (w) => !['kpi', 'stats', 'chart', 'chart_bar', 'chart_line', 'chart_pie', 'table', 'data_table', 'list', 'form', 'form_group'].includes(w.type)
  ) || []

  return (
    <div className="w-full h-full flex min-h-0">
    <div className={`flex-1 min-w-0 overflow-auto ${bgMain} relative flex flex-col`}>
      <ArtifactStateOverlay
        isRegenerating={isRegenerating}
        hasContent={Boolean(data)}
        label="Gerando wireframe do protótipo"
        hint="Telas, layout e widgets serão atualizados automaticamente."
      />
      {data?.is_fallback && <FallbackNotice artifactName="protótipo" onRegenerate={onRegenerate} />}
      <div className="flex h-full min-h-[600px]">
        {/* Sidebar */}
        <aside className={`w-[240px] shrink-0 ${bgSidebar} ${borderColor} border-r flex flex-col`}>
          {/* Logo */}
          <div className={`flex items-center gap-3 px-5 py-4 ${borderColor} border-b`}>
            <WFXBox size={32} />
            <span className={`text-sm font-semibold ${textPrimary} truncate`}>{title}</span>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-0.5">
            {pages.map((page, idx) => {
              const Icon = PAGE_ICONS[page.name] || FileText
              const isActive = idx === activePage
              return (
                <button
                  key={page.name}
                  onClick={() => setActivePage(idx)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? `${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-800'} border-l-[3px] border-gray-700`
                      : `${textSecondary} ${hoverBg}`
                  }`}
                >
                  <Icon size={16} className={isActive ? textPrimary : textMuted} />
                  {page.name}
                </button>
              )
            })}
          </nav>

          {/* User */}
          <div className={`p-4 ${borderColor} border-t`}>
            <div className={`flex items-center gap-3 px-3 py-2.5 border ${borderColor} ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <WFXBox size={32} className="rounded-full" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <WFText w={50} h={10} />
                <WFText w={80} h={8} />
              </div>
              <ChevronDown size={14} className={textMuted} />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className={`flex items-center gap-4 px-6 py-3.5 ${headerBg} ${borderColor} border-b shrink-0 h-14`}>
            <button className={`p-2 ${hoverBg} ${textSecondary}`}>
              <Menu size={18} />
            </button>
            <div className={`flex-1 flex items-center gap-2 px-4 py-2 border ${inputBg} rounded-sm`}>
              <Search size={14} className={textMuted} />
              <span className={`text-sm ${textMuted}`}>Buscar...</span>
            </div>
            <button className={`p-2 ${hoverBg} ${textSecondary} relative`}>
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-gray-400 ring-2 ring-white dark:ring-gray-900" />
            </button>
          </header>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-auto p-6">
            {/* Page Title */}
            <div className="mb-6">
              <h1 className={`text-xl font-semibold ${textPrimary}`}>{currentPage.name}</h1>
              <p className={`text-sm ${textSecondary} mt-1`}>{description}</p>
            </div>

            {/* KPI Cards */}
            {kpiWidgets.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {kpiWidgets.map((w, i) => (
                  <WireframeWidgetRenderer key={i} widget={w} darkMode={darkMode} />
                ))}
              </div>
            )}

            {/* Toolbar */}
            {otherWidgets.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {otherWidgets.map((w, i) => (
                  <WireframeWidgetRenderer key={i} widget={w} darkMode={darkMode} />
                ))}
              </div>
            )}

            {/* Charts + Lists */}
            {(chartWidgets.length > 0 || listWidgets.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {chartWidgets.map((w, i) => (
                  <div key={i} className={chartWidgets.length === 1 && listWidgets.length === 0 ? 'lg:col-span-3' : 'lg:col-span-2'}>
                    <WireframeWidgetRenderer widget={w} darkMode={darkMode} />
                  </div>
                ))}
                {listWidgets.map((w, i) => (
                  <div key={i}>
                    <WireframeWidgetRenderer widget={w} darkMode={darkMode} />
                  </div>
                ))}
              </div>
            )}

            {/* Tables */}
            {tableWidgets.map((w, i) => (
              <div key={i} className="mb-6">
                <WireframeWidgetRenderer widget={w} darkMode={darkMode} />
              </div>
            ))}

            {/* Forms */}
            {formWidgets.map((w, i) => (
              <div key={i} className="mb-6 max-w-xl">
                <WireframeWidgetRenderer widget={w} darkMode={darkMode} />
              </div>
            ))}

            {/* Footer */}
            <div className={`mt-8 text-center text-xs ${textMuted}`}>
              [Wireframe] Protótipo gerado automaticamente pelo agente ArchForge
            </div>
          </div>
        </main>
      </div>
    </div>
    <aside className="w-[240px] shrink-0 border-l border-line bg-surface overflow-auto p-3 flex flex-col gap-2">
      <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Telas</span>
      {pages.length === 0 && <p className="text-xs text-faint">O protótipo lista as telas aqui depois da geração.</p>}
      {pages.map((page, idx) => (
        <button
          key={`${page.name}-${idx}`}
          type="button"
          onClick={() => setActivePage(idx)}
          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left text-[12.5px] cursor-pointer ${
            idx === activePage ? 'border-ink bg-surface-2' : 'border-line'
          }`}
        >
          <span className="font-mono text-[11px] text-faint">{String(idx + 1).padStart(2, '0')}</span>
          {page.name}
        </button>
      ))}
    </aside>
    </div>
  )
}

/* ═══════════════════════════════════════════════
   RENDERERS NÃO-WEB (CLI / API / MOBILE)
   ═══════════════════════════════════════════════ */

interface NonWebProps {
  kind: 'cli' | 'api' | 'mobile'
  data: PrototypeData | null
  darkMode: boolean
  isRegenerating: boolean
  onRegenerate?: () => void
}

const KIND_META: Record<NonWebProps['kind'], { label: string; icon: typeof Terminal }> = {
  cli: { label: 'Interface de linha de comando (CLI)', icon: Terminal },
  api: { label: 'API / serviço', icon: Webhook },
  mobile: { label: 'Aplicativo mobile', icon: Smartphone },
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-emerald-500',
  POST: 'bg-blue-500',
  PUT: 'bg-amber-500',
  PATCH: 'bg-violet-500',
  DELETE: 'bg-red-500',
}

function NonWebPrototype({ kind, data, darkMode, isRegenerating, onRegenerate }: NonWebProps) {
  const bgMain = darkMode ? 'bg-gray-950' : 'bg-gray-100'
  const textPrimary = darkMode ? 'text-gray-100' : 'text-gray-900'
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500'
  const meta = KIND_META[kind]
  const KindIcon = meta.icon
  const hasContent = Boolean(
    (data?.commands?.length ?? 0) || (data?.endpoints?.length ?? 0) || (data?.pages?.length ?? 0),
  )

  return (
    <div className={`w-full h-full overflow-auto ${bgMain} relative flex flex-col`}>
      <ArtifactStateOverlay
        isRegenerating={isRegenerating}
        hasContent={hasContent}
        label="Gerando protótipo"
        hint="A interface é adaptada ao tipo de sistema detectado."
      />
      {data?.is_fallback && <FallbackNotice artifactName="protótipo" onRegenerate={onRegenerate} />}

      {/* Cabeçalho com o tipo de interface detectado */}
      <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'} flex items-center gap-3`}>
        <div className="w-9 h-9 rounded-lg bg-agent/15 text-agent flex items-center justify-center shrink-0">
          <KindIcon size={18} />
        </div>
        <div className="min-w-0">
          <h2 className={`text-sm font-semibold ${textPrimary} truncate`}>{data?.title || 'Protótipo'}</h2>
          <p className={`text-xs ${textSecondary}`}>
            {meta.label}
            {data?.description ? ` · ${data.description}` : ''}
          </p>
        </div>
      </div>

      <div className="flex-1 p-6">
        {kind === 'cli' && <CliView commands={data?.commands ?? []} darkMode={darkMode} title={data?.title} />}
        {kind === 'api' && <ApiView endpoints={data?.endpoints ?? []} darkMode={darkMode} />}
        {kind === 'mobile' && <MobileView pages={data?.pages ?? []} darkMode={darkMode} />}
        <div className={`mt-8 text-center text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          [Protótipo] Interface gerada automaticamente pelo agente ArchForge
        </div>
      </div>
    </div>
  )
}

/** CLI: janela de terminal com comandos e saída de exemplo. */
function CliView({ commands, darkMode, title }: { commands: PrototypeCommand[]; darkMode: boolean; title?: string }) {
  const bin = (title || 'app').toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9-]/g, '') || 'app'
  if (commands.length === 0) {
    return <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Nenhum comando gerado ainda.</p>
  }
  return (
    <div className="max-w-3xl mx-auto rounded-xl overflow-hidden shadow-2xl border border-gray-800">
      {/* Barra de título do terminal */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800">
        <span className="w-3 h-3 rounded-full bg-red-400" />
        <span className="w-3 h-3 rounded-full bg-yellow-400" />
        <span className="w-3 h-3 rounded-full bg-green-400" />
        <span className="ml-2 text-[11px] font-mono text-gray-400">{bin} — terminal</span>
      </div>
      {/* Corpo do terminal */}
      <div className="bg-[#0d1117] p-4 font-mono text-[13px] leading-relaxed space-y-4 overflow-x-auto">
        {commands.map((c, i) => (
          <div key={i}>
            <div className="flex items-start gap-2">
              <span className="text-emerald-400 select-none shrink-0">$</span>
              <span className="text-gray-100 whitespace-pre-wrap break-words">{c.command}</span>
            </div>
            {c.description && <div className="text-gray-500 text-[11px] mt-1 ml-4"># {c.description}</div>}
            {c.example_output && (
              <pre className="text-gray-300 mt-1.5 ml-4 whitespace-pre-wrap break-words border-l-2 border-gray-700 pl-3">
                {c.example_output}
              </pre>
            )}
          </div>
        ))}
        <div className="flex items-center gap-2">
          <span className="text-emerald-400 select-none">$</span>
          <span className="w-2 h-4 bg-gray-400 animate-pulse inline-block" />
        </div>
      </div>
    </div>
  )
}

/** API: lista de endpoints estilo Swagger/Postman. */
function ApiView({ endpoints, darkMode }: { endpoints: PrototypeEndpoint[]; darkMode: boolean }) {
  const cardBg = darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
  const codeBg = darkMode ? 'bg-gray-950 text-gray-300' : 'bg-gray-50 text-gray-700'
  const textPrimary = darkMode ? 'text-gray-100' : 'text-gray-900'
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500'
  if (endpoints.length === 0) {
    return <p className={textSecondary}>Nenhum endpoint gerado ainda.</p>
  }
  return (
    <div className="max-w-3xl mx-auto space-y-3">
      {endpoints.map((e, i) => {
        const method = (e.method || 'GET').toUpperCase()
        return (
          <div key={i} className={`rounded-lg border ${cardBg} overflow-hidden`}>
            <div className="flex items-center gap-3 px-4 py-3">
              <span className={`text-[11px] font-bold text-white px-2 py-1 rounded ${METHOD_COLORS[method] || 'bg-gray-500'} shrink-0`}>
                {method}
              </span>
              <code className={`text-sm font-mono ${textPrimary} break-all`}>{e.path}</code>
            </div>
            {e.description && (
              <p className={`px-4 pb-2 text-xs ${textSecondary}`}>{e.description}</p>
            )}
            {(e.request_example || e.response_example) && (
              <div className="px-4 pb-3 space-y-2">
                {e.request_example && (
                  <div>
                    <div className={`text-[10px] uppercase font-semibold ${textSecondary} mb-1`}>Request</div>
                    <pre className={`text-[11px] font-mono rounded p-2 overflow-x-auto ${codeBg}`}>{e.request_example}</pre>
                  </div>
                )}
                {e.response_example && (
                  <div>
                    <div className={`text-[10px] uppercase font-semibold ${textSecondary} mb-1`}>Response</div>
                    <pre className={`text-[11px] font-mono rounded p-2 overflow-x-auto ${codeBg}`}>{e.response_example}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Mobile: telas dentro de frames de celular, reusando os widgets do wireframe. */
function MobileView({ pages, darkMode }: { pages: WireframePage[]; darkMode: boolean }) {
  const frameBg = darkMode ? 'bg-gray-900' : 'bg-white'
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500'
  if (pages.length === 0) {
    return <p className={textSecondary}>Nenhuma tela gerada ainda.</p>
  }
  return (
    <div className="flex flex-wrap gap-8 justify-center">
      {pages.slice(0, 4).map((page, i) => (
        <div key={i} className="flex flex-col items-center gap-2">
          {/* Frame do celular */}
          <div className={`w-[280px] h-[560px] rounded-[2rem] border-[10px] border-gray-800 ${frameBg} overflow-hidden shadow-xl relative`}>
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-800 rounded-b-xl z-10" />
            <div className="h-full overflow-y-auto pt-7 pb-4 px-3 space-y-3">
              <div className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} px-1`}>{page.name}</div>
              {(page.widgets || []).map((w, wi) => (
                <WireframeWidgetRenderer key={wi} widget={w} darkMode={darkMode} />
              ))}
            </div>
          </div>
          <span className={`text-xs ${textSecondary}`}>{page.route}</span>
        </div>
      ))}
    </div>
  )
}
